import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db, storage } from '../../../shared/storage/firebase';
import { Certification, CertificationStep, CommunityTraining, getQuizQuestions, LearningFile, LearningModule, LearningTraining, TrainingComment, TrainingCommentPath } from '../types';

const byOrder = <T extends { order: number }>(a: T, b: T) => a.order - b.order;
const legacyStepTitles: Record<string, string[]> = {
  ogTraining: ['Overture Teacher Training', 'OG Training', 'Live Observation & Practice'],
};
const modernStepTitles: Record<string, string> = {
  ogTraining: 'Live Q&A session',
};
const REMOVED_TEACHER_CERTIFICATION_STEPS = new Set(['schoolTraining', 'firstClass']);
const modernizeLearningLabels = (certification: Certification): Certification => certification.id === 'overture-teacher' ? {
  ...certification,
  title: ['Overture Teacher Certification', 'Overture Teaching Practice'].includes(certification.title) ? 'Overture Teaching Certification' : certification.title,
  steps: certification.steps
    .filter(step => !REMOVED_TEACHER_CERTIFICATION_STEPS.has(step.id))
    .map(step => step.id === 'ogTraining' ? {
      ...step,
      title: 'Live Q&A session',
      description: 'Attend a biweekly Overture Monday Get Together or schedule a live call with Steven or Aspen to get your questions answered before your first class.',
      completionType: 'acknowledgment',
      linkUrl: 'https://meet.google.com/prm-igmd-ctj',
      linkLabel: 'Join the Monday Get Together',
      writtenContent: 'Overture Monday Get Together 🎉 (Optional)\nMonday, September 28 · 7:00–8:00pm\nTime zone: America/New_York\n\nThese sessions are held biweekly. Attend one session or schedule a live call with Steven or Aspen before your first class.\n\nDial-in: (US) +1 470-443-0028 · PIN: 727 592 552#',
      documents: [...(step.documents || []).filter(document => document.name !== 'More phone numbers'), {
        name: 'More phone numbers',
        url: 'https://tel.meet/prm-igmd-ctj?pin=4759996303504',
        storagePath: 'external:https://tel.meet/prm-igmd-ctj?pin=4759996303504',
        contentType: 'text/html',
        sizeBytes: 0,
      }],
    } : {
      ...step,
      title: legacyStepTitles[step.id]?.includes(step.title) ? modernStepTitles[step.id] : step.title,
    }),
} : certification;

export async function listCertifications(includeDrafts = false): Promise<Certification[]> {
  const certificationRef = collection(db, 'learningCertifications');
  const certificationSnap = await getDocs(includeDrafts ? certificationRef : query(certificationRef, where('published', '==', true)));
  const certifications = await Promise.all(certificationSnap.docs.map(async certificationDoc => {
    const stepRef = collection(db, 'learningCertifications', certificationDoc.id, 'steps');
    const stepSnap = await getDocs(includeDrafts ? stepRef : query(stepRef, where('published', '==', true)));
    const steps = stepSnap.docs
      .map(step => ({ id: step.id, certificationId: certificationDoc.id, ...step.data() } as CertificationStep))
      .filter(step => includeDrafts || step.published)
      .sort(byOrder);
    return { id: certificationDoc.id, ...certificationDoc.data(), steps } as Certification;
  }));
  return certifications.filter(item => includeDrafts || item.published).sort(byOrder).map(modernizeLearningLabels);
}

export async function getCertification(id: string, includeDrafts = false): Promise<Certification | null> {
  const certificationDoc = await getDoc(doc(db, 'learningCertifications', id));
  if (!certificationDoc.exists()) return null;
  const certification = { id: certificationDoc.id, ...certificationDoc.data() } as Certification;
  if (!includeDrafts && !certification.published) return null;
  const stepSnap = await getDocs(collection(db, 'learningCertifications', id, 'steps'));
  const steps = stepSnap.docs
    .map(step => ({ id: step.id, certificationId: id, ...step.data() } as CertificationStep))
    .filter(step => includeDrafts || step.published)
    .sort(byOrder);
  return modernizeLearningLabels({ ...certification, steps });
}

export async function saveCertification(certification: Partial<Certification>): Promise<string> {
  const payload = {
    title: certification.title || 'Untitled certification',
    description: certification.description || '',
    kind: certification.kind || 'standard',
    order: Number(certification.order || 0),
    published: Boolean(certification.published),
    moduleIds: certification.moduleIds || [],
    trainingIds: certification.trainingIds || [],
    pathItems: (certification.pathItems || []).map(item => ({ kind: item.kind, id: item.id })),
    updatedAt: serverTimestamp(),
  };
  if (certification.id) {
    await setDoc(doc(db, 'learningCertifications', certification.id), payload, { merge: true });
    return certification.id;
  }
  const created = await addDoc(collection(db, 'learningCertifications'), { ...payload, createdAt: serverTimestamp() });
  return created.id;
}

export async function listLearningTrainings(includeDrafts = false): Promise<LearningTraining[]> {
  const ref = collection(db, 'learningTrainings');
  const snap = await getDocs(includeDrafts ? ref : query(ref, where('published', '==', true)));
  return snap.docs
    .map(item => ({ id: item.id, ...item.data() } as LearningTraining))
    .filter(item => includeDrafts || item.published)
    .sort((a, b) => a.title.localeCompare(b.title));
}

export async function getLearningTraining(id: string, includeDrafts = false): Promise<LearningTraining | null> {
  const snap = await getDoc(doc(db, 'learningTrainings', id));
  if (!snap.exists()) return null;
  const training = { id: snap.id, ...snap.data() } as LearningTraining;
  return includeDrafts || training.published ? training : null;
}

export async function saveLearningTraining(training: Partial<LearningTraining>): Promise<string> {
  const payload = {
    title: training.title || 'Untitled lesson',
    description: training.description || '',
    writtenContent: training.writtenContent || '',
    learningObjective: training.learningObjective || '',
    durationMinutes: Math.max(0, Number(training.durationMinutes ?? (training.durationSeconds != null ? training.durationSeconds / 60 : 0))),
    thumbnail: training.thumbnail || null,
    video: training.video || null,
    documents: training.documents || [],
    linkUrl: training.linkUrl || '',
    linkLabel: training.linkLabel || '',
    tags: training.tags || [],
    searchKeywords: training.searchKeywords || [],
    audience: training.audience?.length ? training.audience : ['teachers'],
    completionType: training.completionType || 'video',
    quizPrompt: training.quizPrompt || '',
    quizQuestions: (training.quizQuestions || []).filter(question => question?.prompt?.trim()).map(question => ({
      id: question.id,
      prompt: question.prompt.trim(),
    })),
    published: Boolean(training.published),
    communityEducation: Boolean(training.communityEducation),
    updatedAt: serverTimestamp(),
  };
  if (training.id) {
    await setDoc(doc(db, 'learningTrainings', training.id), payload, { merge: true });
    return training.id;
  }
  const created = await addDoc(collection(db, 'learningTrainings'), { ...payload, createdAt: serverTimestamp() });
  return created.id;
}

export async function deleteLearningTraining(id: string) {
  await deleteDoc(doc(db, 'learningTrainings', id));
}

export async function listLearningModules(includeDrafts = false): Promise<LearningModule[]> {
  const ref = collection(db, 'learningModules');
  const snap = await getDocs(includeDrafts ? ref : query(ref, where('published', '==', true)));
  return snap.docs
    .map(item => ({ id: item.id, ...item.data() } as LearningModule))
    .filter(item => includeDrafts || item.published)
    .sort((a, b) => a.title.localeCompare(b.title));
}

export async function saveLearningModule(module: Partial<LearningModule>): Promise<string> {
  const payload = {
    title: module.title || 'Untitled module',
    description: module.description || '',
    trainingIds: module.trainingIds || [],
    published: Boolean(module.published),
    updatedAt: serverTimestamp(),
  };
  if (module.id) {
    await setDoc(doc(db, 'learningModules', module.id), payload, { merge: true });
    return module.id;
  }
  const created = await addDoc(collection(db, 'learningModules'), { ...payload, createdAt: serverTimestamp() });
  return created.id;
}

export async function deleteLearningModule(id: string) {
  await deleteDoc(doc(db, 'learningModules', id));
}

export async function deleteCertification(id: string) {
  const steps = await getDocs(collection(db, 'learningCertifications', id, 'steps'));
  await Promise.all(steps.docs.map(step => deleteDoc(step.ref)));
  await deleteDoc(doc(db, 'learningCertifications', id));
}

export async function saveCertificationStep(certificationId: string, step: Partial<CertificationStep>): Promise<string> {
  const payload = {
    title: step.title || 'Untitled lesson',
    description: step.description || '',
    writtenContent: step.writtenContent || '',
    completionType: step.completionType || 'acknowledgment',
    video: step.video || null,
    documents: step.documents || [],
    quizPrompt: step.quizPrompt || getQuizQuestions(step)[0]?.prompt || '',
    quizQuestions: getQuizQuestions(step),
    linkUrl: step.linkUrl || '',
    linkLabel: step.linkLabel || '',
    onboardingTaskId: step.onboardingTaskId || '',
    required: step.required !== false,
    order: Number(step.order || 0),
    published: step.published !== false,
    updatedAt: serverTimestamp(),
  };
  if (step.id) {
    await setDoc(doc(db, 'learningCertifications', certificationId, 'steps', step.id), payload, { merge: true });
    return step.id;
  }
  const created = await addDoc(collection(db, 'learningCertifications', certificationId, 'steps'), {
    ...payload,
    createdAt: serverTimestamp(),
  });
  return created.id;
}

export async function deleteCertificationStep(certificationId: string, stepId: string) {
  await deleteDoc(doc(db, 'learningCertifications', certificationId, 'steps', stepId));
}

export async function listCommunityTrainings(includeUnapproved = false): Promise<CommunityTraining[]> {
  const trainingRef = collection(db, 'communityTrainings');
  const snap = await getDocs(includeUnapproved ? trainingRef : query(trainingRef, where('status', '==', 'approved')));
  return snap.docs
    .map(item => ({ id: item.id, ...item.data() } as CommunityTraining))
    .filter(item => includeUnapproved || item.status === 'approved')
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')));
}

export async function saveCommunityTraining(training: Partial<CommunityTraining>): Promise<string> {
  const payload = {
    title: training.title || 'Untitled lesson',
    description: training.description || '',
    writtenContent: training.writtenContent || '',
    video: training.video || null,
    documents: training.documents || [],
    authorId: training.authorId || '',
    authorName: training.authorName || 'Overture teacher',
    status: training.status || 'pending',
    moderationNote: training.moderationNote || '',
    updatedAt: serverTimestamp(),
  };
  if (training.id) {
    await setDoc(doc(db, 'communityTrainings', training.id), payload, { merge: true });
    return training.id;
  }
  const created = await addDoc(collection(db, 'communityTrainings'), { ...payload, createdAt: serverTimestamp() });
  return created.id;
}

export async function moderateCommunityTraining(id: string, status: 'approved' | 'rejected', note = '') {
  await updateDoc(doc(db, 'communityTrainings', id), {
    status,
    moderationNote: note,
    moderatedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteCommunityTraining(id: string) {
  await deleteDoc(doc(db, 'communityTrainings', id));
}

export async function listTrainingComments(path: TrainingCommentPath): Promise<TrainingComment[]> {
  const snap = await getDocs(collection(db, path.collection, path.id, 'comments'));
  return snap.docs
    .map(item => ({ id: item.id, ...item.data() } as TrainingComment))
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')));
}

export async function addTrainingComment(path: TrainingCommentPath, comment: { authorId: string; authorName: string; text: string }) {
  const text = comment.text.trim();
  if (!text) return;
  await addDoc(collection(db, path.collection, path.id, 'comments'), {
    authorId: comment.authorId,
    authorName: comment.authorName,
    text,
    createdAt: serverTimestamp(),
  });
}

export async function deleteTrainingComment(path: TrainingCommentPath, commentId: string) {
  await deleteDoc(doc(db, path.collection, path.id, 'comments', commentId));
}

export async function uploadLearningFile(path: string, file: File): Promise<LearningFile> {
  const cleanName = file.name.replace(/[^a-zA-Z0-9._-]/g, '-');
  const storagePath = `${path}/${Date.now()}-${cleanName}`;
  const fileRef = ref(storage, storagePath);
  await uploadBytes(fileRef, file, { contentType: file.type || 'application/octet-stream' });
  return {
    name: file.name,
    url: await getDownloadURL(fileRef),
    storagePath,
    contentType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  };
}

export async function saveStepProgress(
  teacherId: string,
  certificationId: string,
  stepId: string,
  data: Record<string, unknown>
) {
  await setDoc(doc(db, 'teacherLearningProgress', teacherId, 'steps', `${certificationId}_${stepId}`), {
    teacherId,
    certificationId,
    stepId,
    ...data,
    updatedAt: serverTimestamp(),
  }, { merge: true });
}

export type LearningProgressEntry = {
  id: string;
  teacherId: string;
  certificationId: string;
  stepId: string;
  status: string;
  evidence?: LearningFile;
  quizAnswer?: string;
  quizAnswers?: Record<string, string>;
  teacherName?: string;
  teacherEmail?: string;
  teacherHeadshotUrl?: string;
  teacherStaffStatus?: string;
};

export async function listTeacherLearningProgress(teacherId: string): Promise<LearningProgressEntry[]> {
  const snap = await getDocs(collection(db, 'teacherLearningProgress', teacherId, 'steps'));
  return snap.docs.map(item => ({ id: item.id, ...item.data() } as LearningProgressEntry));
}

export async function listLearningProgress(): Promise<LearningProgressEntry[]> {
  // Do not use collectionGroup('steps') here: certification definitions also use
  // a `steps` subcollection, so Firestore cannot prove that a collection-group
  // query is authorized by the path-specific progress rules.
  const teacherSnap = await getDocs(query(collection(db, 'users'), where('role', '==', 'teacher')));
  const perTeacher = await Promise.all(
    teacherSnap.docs.map(async teacher => {
      const teacherData = teacher.data();
      const entries = await listTeacherLearningProgress(teacher.id);
      return entries.map(entry => ({
        ...entry,
        teacherName: String(teacherData.displayName || teacherData.email || 'Unnamed teacher'),
        teacherEmail: String(teacherData.email || ''),
        teacherHeadshotUrl: String(teacherData.headshotUrl || ''),
        teacherStaffStatus: String(teacherData.staffStatus || 'active'),
      }));
    })
  );
  return perTeacher.flat();
}

export async function reviewStepProgress(entry: LearningProgressEntry, status: 'completed' | 'rejected') {
  await setDoc(doc(db, 'teacherLearningProgress', entry.teacherId, 'steps', entry.id), {
    status,
    reviewedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  }, { merge: true });
  if (entry.stepId === 'backgroundCheck') {
    try {
      await reviewOnboardingBackgroundCheck(entry.teacherId, status);
    } catch (error) {
      console.error('[learningCenterService] Could not update Checkr hiring status', error);
    }
  }
}

export type OnboardingReviewRequest = {
  teacherId: string;
  taskId: 'backgroundCheck';
  status: 'pending_review';
  requestedAt?: unknown;
  teacherName: string;
  teacherEmail: string;
  teacherHeadshotUrl: string;
  teacherStaffStatus: string;
};

export async function listPendingOnboardingReviews(): Promise<OnboardingReviewRequest[]> {
  try {
    const [usersSnap, teachersSnap] = await Promise.all([
      getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
      getDocs(collection(db, 'teachers')),
    ]);
    const usersById = Object.fromEntries(usersSnap.docs.map(item => [item.id, item.data()]));
    return teachersSnap.docs.flatMap(item => {
      const hiring = (item.data() as any)?.hiring || {};
      if (hiring?.onboarding?.backgroundCheck?.status !== 'pending_review') return [];
      const user = usersById[item.id] || {};
      return [{
        teacherId: item.id,
        taskId: 'backgroundCheck' as const,
        status: 'pending_review' as const,
        requestedAt: hiring.onboarding.backgroundCheck.requestedAt || null,
        teacherName: String(user.displayName || user.email || 'Unnamed teacher'),
        teacherEmail: String(user.email || ''),
        teacherHeadshotUrl: String(user.headshotUrl || ''),
        teacherStaffStatus: String(user.staffStatus || 'applicant'),
      }];
    });
  } catch (error) {
    console.error('[learningCenterService] Could not load Checkr hiring reviews', error);
    return [];
  }
}

export type PendingStateBackgroundCheck = {
  teacherId: string;
  checkType: string;
  jurisdiction: string;
  title: string;
  formSubmittedAt?: unknown;
  teacherName: string;
  teacherEmail: string;
  resultFile?: LearningFile | null;
};

export async function listPendingStateBackgroundChecks(): Promise<PendingStateBackgroundCheck[]> {
  const [usersSnap, teachersSnap] = await Promise.all([
    getDocs(query(collection(db, 'users'), where('role', '==', 'teacher'))),
    getDocs(collection(db, 'teachers')),
  ]);
  const usersById = Object.fromEntries(usersSnap.docs.map(item => [item.id, item.data()]));
  return teachersSnap.docs.flatMap(item => {
    const checks = (item.data() as any)?.hiring?.stateBackgroundChecks || {};
    const user: any = usersById[item.id] || {};
    return Object.values(checks).flatMap((raw: any) => raw?.status === 'pending_run' ? [{
      teacherId: item.id,
      checkType: String(raw.checkType || ''),
      jurisdiction: String(raw.jurisdiction || ''),
      title: String(raw.title || 'State background check'),
      formSubmittedAt: raw.formSubmittedAt || null,
      teacherName: String(user.displayName || user.email || 'Unnamed teacher'),
      teacherEmail: String(user.email || ''),
      resultFile: raw.resultFile || null,
    }] : []);
  });
}

export async function approveStateBackgroundCheck(teacherId: string, checkType: string) {
  const callable = httpsCallable(getFunctions(), 'hiringAdminApproveStateBackgroundCheck');
  await callable({ teacherId, checkType });
}

export async function reviewOnboardingBackgroundCheck(
  teacherId: string,
  status: 'completed' | 'rejected'
) {
  const completed = status === 'completed';
  await Promise.all([
    updateDoc(doc(db, 'teachers', teacherId), {
      'hiring.onboarding.backgroundCheck.status': completed ? 'completed' : 'not_started',
      'hiring.onboarding.backgroundCheck.completedAt': completed ? serverTimestamp() : null,
      'hiring.onboarding.backgroundCheck.reviewedAt': serverTimestamp(),
      updatedAt: serverTimestamp(),
      ...(completed ? {} : { 'hiring.onboarding.backgroundCheck.requestedAt': null }),
    }),
    setDoc(doc(db, 'users', teacherId), {
      backgroundCheckCleared: completed,
      updatedAt: serverTimestamp(),
    }, { merge: true }),
  ]);
}
